/**
 * Curtain measurements — multi-size shared utility.
 *
 * Mirrors src/lib/carpetMeasurements.ts: customer / admin can now add
 * curtains of different sizes (each row = one or more panels of a specific
 * width × length) to the same booking. New bookings persist
 * `measurements.curtainItems[]`; legacy bookings still carry the flat
 * `curtainWidth` / `curtainLength` / `numberOfPanels` fields and are read
 * through the same normalizer for back-compat.
 *
 * Pricing model: total sq.ft × basePrice (₹/sqft). Each row contributes
 * (width × length × quantity) sq.ft (or m², converted internally).
 */

import type { MeasurementUnit } from '@/lib/carpetMeasurements';
import { SQM_TO_SQFT, getMeasurementUnit } from '@/lib/carpetMeasurements';

export type { MeasurementUnit };

export interface CurtainItem {
  /** Optional room / window note shown to the technician. */
  label?: string;
  /** In whatever `measurementUnit` the booking is using. */
  width: number;
  length: number;
  /** Number of panels that share this exact size. */
  quantity: number;
}

function readSm(meas: unknown): Record<string, unknown> {
  if (!meas || typeof meas !== 'object') return {};
  const m = meas as Record<string, unknown>;
  if (
    m.serviceMeasurements &&
    typeof m.serviceMeasurements === 'object' &&
    !Array.isArray(m.serviceMeasurements)
  ) {
    return m.serviceMeasurements as Record<string, unknown>;
  }
  return m;
}

/**
 * Normalised CurtainItem[] from either the new array shape or the legacy
 * single-size flat fields. Invalid rows are filtered so callers can sum
 * safely without per-row guards.
 */
export function normalizeCurtainItems(meas: unknown): CurtainItem[] {
  const sm = readSm(meas);
  const raw = sm.curtainItems;
  if (Array.isArray(raw) && raw.length > 0) {
    const out: CurtainItem[] = [];
    for (const r of raw) {
      if (!r || typeof r !== 'object') continue;
      const item = r as Record<string, unknown>;
      const width = Number(item.width) || 0;
      const length = Number(item.length) || 0;
      const quantity = Number(item.quantity) || 0;
      if (width <= 0 || length <= 0 || quantity <= 0) continue;
      out.push({
        label: typeof item.label === 'string' ? item.label : '',
        width,
        length,
        quantity,
      });
    }
    return out;
  }
  // Legacy single-size fallback — preserves pricing/display for bookings
  // saved before multi-size landed.
  const width = Number(sm.curtainWidth) || 0;
  const length = Number(sm.curtainLength) || 0;
  const quantity = Number(sm.numberOfPanels) || 0;
  if (width > 0 && length > 0 && quantity > 0) {
    return [{ width, length, quantity }];
  }
  return [];
}

/** Total panel count across all rows. */
export function curtainTotalPanels(meas: unknown): number {
  return normalizeCurtainItems(meas).reduce((sum, c) => sum + c.quantity, 0);
}

/** Total curtain area in square FEET (pricing unit). */
export function curtainTotalAreaSqFt(meas: unknown): number {
  const items = normalizeCurtainItems(meas);
  if (items.length === 0) return 0;
  const isMeters = getMeasurementUnit(meas) === 'meters';
  const raw = items.reduce((sum, c) => sum + c.width * c.length * c.quantity, 0);
  return isMeters ? raw * SQM_TO_SQFT : raw;
}

/**
 * Line cost: round(totalSqFt × pricePerSqFt). Returns null when no rows
 * are present so callers can fall back / show ₹0 cleanly.
 */
export function curtainLineCost(
  meas: unknown,
  pricePerSqFt: number
): number | null {
  if (!Number.isFinite(pricePerSqFt) || pricePerSqFt <= 0) return null;
  const items = normalizeCurtainItems(meas);
  if (items.length === 0) return null;
  return Math.round(curtainTotalAreaSqFt(meas) * pricePerSqFt);
}

/**
 * Human-readable measurement summary used by the pricing sidebar, emails,
 * dashboards, and invoices.
 *
 *   - Empty:                          ""
 *   - Single row, no rate:            "8×6 ft × 4 panels"
 *   - Single row, with rate:          "8×6 ft × 4 panels × ₹70/sq.ft"
 *   - Multi rows, no rate:            "10 panels across 3 sizes (192 sq.ft)"
 *   - Multi rows, with rate:          "10 panels across 3 sizes (192 sq.ft × ₹70/sq.ft)"
 *
 * `pricePerSqft` is optional — display surfaces that don't have the live
 * basePrice handy can still call this without backfilling the rate. The
 * booking flow sidebar passes it so the line mirrors the sofa breakdown
 * ("2 seats × ₹1,100").
 */
export function curtainDescription(meas: unknown, pricePerSqft?: number): string {
  const items = normalizeCurtainItems(meas);
  if (items.length === 0) return '';
  const unit = getMeasurementUnit(meas);
  const unitLabel = unit === 'meters' ? 'm' : 'ft';
  const ratePart =
    pricePerSqft && pricePerSqft > 0
      ? ` × ₹${pricePerSqft.toLocaleString('en-IN')}/sq.ft`
      : '';
  if (items.length === 1) {
    const c = items[0];
    return `${c.width}×${c.length} ${unitLabel} × ${c.quantity} panel${c.quantity !== 1 ? 's' : ''}${ratePart}`;
  }
  const totalPanels = curtainTotalPanels(meas);
  const totalSqFt = Math.round(curtainTotalAreaSqFt(meas));
  return `${totalPanels} panels across ${items.length} sizes (${totalSqFt} sq.ft${ratePart})`;
}

/** Per-row description for itemised renders (e.g. job sheets). */
export function curtainItemDescription(item: CurtainItem, unit: MeasurementUnit): string {
  const unitLabel = unit === 'meters' ? 'm' : 'ft';
  const base = `${item.width}×${item.length} ${unitLabel} × ${item.quantity}`;
  return item.label ? `${item.label} — ${base}` : base;
}

/** One auditable curtain line — mirrors CarpetLineItem. */
export interface CurtainLineItem {
  label?: string;
  width: number;
  length: number;
  quantity: number;
  unitLabel: 'ft' | 'm';
  areaSqFt: number;
  amount: number;
}

/**
 * Explodes a multi-size curtain measurement into per-row line items so the
 * pricing breakdown can show each curtain's `dims → area × rate = amount`
 * instead of the collapsed "N panels across M sizes" summary. Rows sum
 * exactly to curtainLineCost(meas, rate) — see carpetLineItems for the
 * reconciliation rationale. Returns [] when no usable rows / no rate.
 */
export function curtainLineItems(
  meas: unknown,
  pricePerSqFt: number,
): CurtainLineItem[] {
  if (!Number.isFinite(pricePerSqFt) || pricePerSqFt <= 0) return [];
  const items = normalizeCurtainItems(meas);
  if (items.length === 0) return [];
  const isMeters = getMeasurementUnit(meas) === 'meters';
  const unitLabel: 'ft' | 'm' = isMeters ? 'm' : 'ft';

  const rows = items.map((c) => {
    const rawArea = c.width * c.length * c.quantity;
    const areaSqFtExact = isMeters ? rawArea * SQM_TO_SQFT : rawArea;
    return {
      label: c.label,
      width: c.width,
      length: c.length,
      quantity: c.quantity,
      unitLabel,
      areaSqFt: Math.round(areaSqFtExact),
      amount: Math.round(areaSqFtExact * pricePerSqFt),
    };
  });

  const authoritative = curtainLineCost(meas, pricePerSqFt) ?? 0;
  const summed = rows.reduce((s, r) => s + r.amount, 0);
  const drift = authoritative - summed;
  if (drift !== 0) {
    rows[rows.length - 1].amount += drift;
  }
  return rows;
}
